// This file is part of B2SHARE
// Copyright (C) 2025 CSC
//
// Invenio RDM Records is free software; you can redistribute it and/or modify it
// under the terms of the MIT License; see LICENSE file for more details.

import React from "react";
import {
  FacebookShareButton,
  TwitterShareButton,
  LinkedinShareButton,
  RedditShareButton,
  EmailShareButton,
  FacebookIcon,
  TwitterIcon,
  LinkedinIcon,
  RedditIcon,
  EmailIcon,
} from "react-share";
import PropTypes from "prop-types";
import { FaMastodon } from "react-icons/fa6";
import { BsCloud } from "react-icons/bs"; // Using as Bluesky placeholder

import { i18next } from "@translations/invenio_app_rdm/i18next";

export const ShareIconButton = ({ onClick, ariaLabel, bgclass, Icon }) => (
  <button
    type="button"
    onClick={onClick}
    aria-label={ariaLabel}
    title={ariaLabel}
    className={`react-share__ShareButton custom-share-button ${bgclass}`}
  >
    <Icon className="share-icons-custom" aria-hidden="true" />
  </button>
);

ShareIconButton.propTypes = {
  onClick: PropTypes.func.isRequired,
  ariaLabel: PropTypes.string.isRequired,
  bgclass: PropTypes.string.isRequired,
  Icon: PropTypes.elementType.isRequired,
};

export const ShareButtons = ({ record, config = {} }) => {
  const title = record.metadata?.title || record.metadata?.titles?.[0]?.title || "";
  const doiURL = record.pids?.doi?.identifier
    ? `https://doi.org/${record.pids.doi.identifier}`
    : window.location.href;

  const citationText = `${title} ${doiURL}`;

  const handleMastodonShare = () => {
    const instance = prompt("Enter your Mastodon instance (e.g., mastodon.social):");
    if (instance) {
      const url = `https://${instance}/share?text=${encodeURIComponent(citationText)}`;
      window.open(url, "_blank", "noopener,noreferrer");
    }
  };

  const handleBlueskyShare = () => {
    navigator.clipboard
      .writeText(citationText)
      .then(() => {
        alert(
          "Citation copied to clipboard. You can now paste it into your Bluesky post."
        );
        window.open("https://bsky.app/", "_blank", "noopener,noreferrer");
      })
      .catch(() => {
        alert("Failed to copy citation. Please copy it manually:\n\n" + citationText);
        window.open("https://bsky.app/", "_blank", "noopener,noreferrer");
      });
  };

  const isEnabled = (key) => config[key] !== false;

  return (
    <div className="share-section rel-mt-2">
      <h3 className="ui tiny header rel-mb-1">
        {i18next.t("Share")}
      </h3>
      <div
        className="share-icon-div"
        role="group"
        aria-label={i18next.t("Share this record")}
      >
      {isEnabled("twitter") && (
        <div className="share-icon-wrapper" title="Share on Twitter.">
          <TwitterShareButton url={doiURL} title={title}>
            <TwitterIcon size={32} round />
          </TwitterShareButton>
        </div>
      )}
      {isEnabled("facebook") && (
        <div className="share-icon-wrapper" title="Share on Facebook.">
          <FacebookShareButton url={doiURL}>
            <FacebookIcon size={32} round />
          </FacebookShareButton>
        </div>
      )}
      {isEnabled("linkedin") && (
        <div
          className="share-icon-wrapper"
          title="Copy the citation before sharing on LinkedIn."
        >
          <LinkedinShareButton url={doiURL}>
            <LinkedinIcon size={32} round />
          </LinkedinShareButton>
        </div>
      )}
      {isEnabled("reddit") && (
        <div className="share-icon-wrapper" title="Share on Reddit.">
          <RedditShareButton url={doiURL} title={title}>
            <RedditIcon size={32} round />
          </RedditShareButton>
        </div>
      )}
      {isEnabled("email") && (
        <div className="share-icon-wrapper" title="Share on email.">
          <EmailShareButton url={doiURL} subject={title} body={citationText}>
            <EmailIcon size={32} round />
          </EmailShareButton>
        </div>
      )}
      {isEnabled("mastodon") && (
        <div className="share-icon-wrapper">
          <ShareIconButton
            onClick={handleMastodonShare}
            ariaLabel={i18next.t("Share on Mastodon")}
            bgclass="share-icon-mastodon-color"
            Icon={FaMastodon}
          />
        </div>
      )}
      {isEnabled("bluesky") && (
        <div
          className="share-icon-wrapper"
          title="Copy the citation before sharing on Bluesky."
        >
          <ShareIconButton
            onClick={handleBlueskyShare}
            ariaLabel={i18next.t("Copy citation and open Bluesky")}
            bgclass="share-icon-bluesky-color"
            Icon={BsCloud}
          />
        </div>
        )}
      </div>
    </div>
  );
};

ShareButtons.propTypes = {
  record: PropTypes.object.isRequired,
  config: PropTypes.object,
};
